import { __rest } from "tslib";
import { createElement as _createElement } from "react";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { getFutureMemberSubscription, getMemberPaymentCards, isEmptyArray } from '@vista/omnichannel-components-domain';
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { MemberSubscriptionStatus } from '@vista-digital/ocapi-types/v1';
import { observer } from 'mobx-react';
import { useContext, useEffect, useMemo, useReducer } from 'react';
import { useI18n } from '../../../context/occ/hooks/use-i18n';
import { makePrimaryCard } from '../actions/make-primary-card';
import { MemberPaymentCardWalletContextInstance } from '../types/context';
import { getLabelForRecurringSubscription } from '../utils/get-label-for-recurring-subscription';
import { memberPaymentCardWalletViewActionReducer } from '../utils/view/view-action-reducer';
import { MemberPaymentCardWalletViewActionType } from '../utils/view/view-action-type';
import { MemberPaymentCardWalletAddCardButton } from './add-card-button';
import { MemberPaymentCardWalletCard } from './card';
import { MemberPaymentCardWalletForm } from './form';
import { MemberPaymentCardWalletMakePrimaryModal } from './modals/make-primary-modal';
import { MemberPaymentCardWalletRemoveModal } from './modals/remove-modal';
const b = bemClassBuilder('v-member-payment-card-wallet-view');
const defaultMemberPaymentCardWalletViewState = {
    showRemoveModal: false,
    showMakePrimaryModal: false,
    canRemove: false,
    canReactivate: false,
    isAddingCard: false
};
export const MemberPaymentCardWalletView = observer(props => {
    var _a, _b;
    const i18n = useI18n();
    const { subscription, disableButtons, onActionStarted, onActionFinished } = props, formProps = __rest(props, ["subscription", "disableButtons", "onActionStarted", "onActionFinished"]);
    const { config, assets, displayText, events } = useContext(MemberPaymentCardWalletContextInstance);
    const { cardTypes, showAddCardFormInline, requirePrimaryCard } = config;
    const { onFormOpenStateChange } = events;
    const cards = (_a = getMemberPaymentCards()) !== null && _a !== void 0 ? _a : [];
    const sortedCards = [...cards].sort(compareMemberPaymentCards);
    const showFormByDefault = requirePrimaryCard && isEmptyArray(cards);
    const [state, dispatch] = useReducer(memberPaymentCardWalletViewActionReducer, Object.assign(Object.assign({}, defaultMemberPaymentCardWalletViewState), { isAddingCard: showFormByDefault }));
    const { showRemoveModal, showMakePrimaryModal, cardIdBeingRemoved, cardIdBeingMadePrimary, isAddingCard } = state;
    const showCards = showAddCardFormInline || !isAddingCard;
    const isRecurringSubscription = (subscription === null || subscription === void 0 ? void 0 : subscription.billingDetails.isRecurring) || false;
    const subscriptionStatus = subscription === null || subscription === void 0 ? void 0 : subscription.status;
    const futureSubscription = getFutureMemberSubscription();
    const hasFutureRecurringSubscription = futureSubscription === null || futureSubscription === void 0 ? void 0 : futureSubscription.billingDetails.isRecurring;
    const subscriptionLabel = useMemo(() => getLabelForRecurringSubscription(subscription), [subscription]);
    const futureSubscriptionLabel = useMemo(() => getLabelForRecurringSubscription(futureSubscription), [futureSubscription]);
    const hideModal = () => dispatch({ type: MemberPaymentCardWalletViewActionType.CloseModal });
    const makePrimaryCallStarted = (card) => {
        dispatch({ type: MemberPaymentCardWalletViewActionType.MakePrimaryCallStarted, card });
        onActionStarted();
    };
    const removeCallStarted = () => {
        dispatch({ type: MemberPaymentCardWalletViewActionType.RemoveCallStarted });
        onActionStarted();
    };
    const handleCallFinished = () => {
        onActionFinished();
        dispatch({ type: MemberPaymentCardWalletViewActionType.CallFinished });
    };
    const handleRemoveButtonClicked = (card) => {
        if (disableButtons)
            return;
        const canRemove = !(card.isPrimary && (isRecurringSubscription || hasFutureRecurringSubscription));
        dispatch({ type: MemberPaymentCardWalletViewActionType.RemoveButtonClicked, card, canRemove });
    };
    const handleMakePrimaryButtonClicked = async (card) => {
        if (disableButtons)
            return;
        if (!isRecurringSubscription) {
            makePrimaryCallStarted(card);
            await makePrimaryCard(card, displayText, i18n);
            handleCallFinished();
        }
        else {
            const canReactivate = subscriptionStatus === MemberSubscriptionStatus.Frozen;
            dispatch({ type: MemberPaymentCardWalletViewActionType.MakePrimaryButtonClicked, card, canReactivate });
        }
    };
    const handleAddButtonClicked = () => {
        if (disableButtons)
            return;
        dispatch({ type: MemberPaymentCardWalletViewActionType.UpdateIsAddingCard, isAddingCard: true });
    };
    const handleCloseFormClicked = () => {
        dispatch({ type: MemberPaymentCardWalletViewActionType.UpdateIsAddingCard, isAddingCard: false });
    };
    useEffect(() => {
        onFormOpenStateChange === null || onFormOpenStateChange === void 0 ? void 0 : onFormOpenStateChange(isAddingCard);
    }, [onFormOpenStateChange, isAddingCard]);
    const removeModalProps = {
        show: showRemoveModal,
        canRemove: state.canRemove,
        card: state.card,
        hideModal,
        onActionStarted: removeCallStarted,
        onActionFinished: handleCallFinished
    };
    const makePrimaryModalProps = {
        show: showMakePrimaryModal,
        canReactivate: state.canReactivate,
        reactivationPrice: (_b = subscription === null || subscription === void 0 ? void 0 : subscription.billingDetails) === null || _b === void 0 ? void 0 : _b.reactivationPrice,
        card: state.card,
        hideModal,
        onActionStarted: makePrimaryCallStarted,
        onActionFinished: handleCallFinished
    };
    const memberPaymentCardWalletCardBaseProps = {
        subscriptionLabel,
        futureSubscriptionLabel,
        onRemoveButtonClicked: handleRemoveButtonClicked,
        disableButtons,
        onMakePrimaryButtonClicked: handleMakePrimaryButtonClicked,
        cardTypes,
        cardIdBeingRemoved,
        cardIdBeingMadePrimary
    };
    return (_jsxs("div", Object.assign({ className: b.block() }, { children: [_jsx(MemberPaymentCardWalletRemoveModal, Object.assign({}, removeModalProps)), _jsx(MemberPaymentCardWalletMakePrimaryModal, Object.assign({}, makePrimaryModalProps)), showCards &&
                sortedCards.map(card => {
                    return (_createElement(MemberPaymentCardWalletCard, Object.assign({}, memberPaymentCardWalletCardBaseProps, { key: card.id, card: card })));
                }), isAddingCard ? (_jsx(MemberPaymentCardWalletForm, Object.assign({}, formProps, { subscription: subscription, showCancelButton: !showFormByDefault, closeForm: handleCloseFormClicked }))) : (_jsx(MemberPaymentCardWalletAddCardButton, { onClick: handleAddButtonClicked, disabled: disableButtons, iconName: assets.addCardButtonIconName, label: displayText.addCardButtonLabel }))] })));
});
MemberPaymentCardWalletView.displayName = 'MemberPaymentCardWalletView';
const compareMemberPaymentCards = (cardA, cardB) => {
    if (cardA.isPrimary)
        return -1;
    if (cardB.isPrimary)
        return 1;
    return 0;
};
